import { Component, type ReactNode, type ErrorInfo } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * App-level error boundary.
 *
 * On low-RAM Android devices a chunk load failure or a post-camera render
 * crash used to unmount React entirely, leaving the user staring at a white
 * WebView (which looks like the app silently closed). This boundary catches
 * those failures and renders a recovery screen with a Reload button.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  private handleReload = () => {
    try {
      sessionStorage.removeItem("chunk_reload");
    } catch { /* ignore */ }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm text-center space-y-4">
          <h1 className="text-lg font-semibold text-foreground">
            Something went wrong
          </h1>
          <p className="text-sm text-muted-foreground">
            The app ran into an unexpected error. Tap Reload to continue.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground"
          >
            Reload app
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;